export const EMPTY_FILTERS = { fechaInicio: "", fechaFin: "", grupos: [] };
export const DEFAULT_QUERY = { search: "", page: 0, pageSize: 10, sort: "ID", direction: "desc" };
export const SLA_HOURS = 24;
const DAY = 86400000;
const dateFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Bogota",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
export const countFormat = (value) => Number(value || 0).toLocaleString("es-CO");
export const hoursFormat = (value) =>
  value === null
    ? "Sin información"
    : `${Number(value).toLocaleString("es-CO", { maximumFractionDigits: 2 })} h`;
export function validDay(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}
export function calendarDay(value) {
  if (!value || typeof value !== "string" || !validDay(value.slice(0, 10))) return "";
  if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(value)) return value.slice(0, 10);
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const parts = dateFormatter.formatToParts(date);
  const part = (name) => parts.find((p) => p.type === name).value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}
export function timestamp(value) {
  if (!calendarDay(value)) return null;
  const zoned = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  const full = value.length === 10 ? `${value}T00:00:00` : value.replace(" ", "T");
  const result = new Date(zoned ? value : `${full}-05:00`).getTime();
  return Number.isFinite(result) ? result : null;
}
export function validateFilters(filters) {
  if (
    (filters.fechaInicio && !validDay(filters.fechaInicio)) ||
    (filters.fechaFin && !validDay(filters.fechaFin))
  )
    return "Selecciona fechas válidas.";
  if (filters.fechaInicio && filters.fechaFin && filters.fechaInicio > filters.fechaFin)
    return "La fecha de inicio no puede ser posterior a la fecha de fin.";
  return "";
}
export function extractTickets(input) {
  let response = input;
  if (typeof response === "string") {
    try {
      response = JSON.parse(response);
    } catch {
      throw new Error("El servicio devolvió un JSON incompleto o inválido. Intenta nuevamente.");
    }
  }
  if (response?.code !== undefined && response.code !== 0) {
    throw new Error(response.message || "El servicio no pudo consultar los tickets.");
  }
  const rows = Array.isArray(response) ? response : response?.data;
  if (!Array.isArray(rows))
    throw new Error("El servicio devolvió un formato de tickets inesperado.");
  const tickets = Array.isArray(rows[0]) ? rows[0] : rows;
  if (tickets.some((row) => !row || typeof row !== "object" || Array.isArray(row)))
    throw new Error("El servicio contiene registros de tickets inválidos.");
  return tickets;
}
const text = (value, fallback) => String(value ?? "").trim() || fallback;
const normalizedText = (value) =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
export function normalizeTickets(rows) {
  const states = {
    resuelto: "Resuelto",
    cerrado: "Cerrado",
    "en curso": "En curso",
    pendiente: "Pendiente",
    nuevo: "Nuevo",
    "en espera": "En espera",
  };
  return rows.map((row, index) => {
    const rawHours = row.Estadisticas_tiempo_solucion_horas;
    const number =
      typeof rawHours === "number" || (typeof rawHours === "string" && rawHours.trim())
        ? Number(rawHours)
        : NaN;
    const ticket = {
      ID: row.ID ?? "",
      Titulo: text(row.Titulo, "Sin título"),
      Descripcion: text(row.Descripcion, "Sin descripción"),
      Estado:
        states[normalizedText(text(row.Estado, "Sin estado"))] || text(row.Estado, "Sin estado"),
      Prioridad: text(row.Prioridad, "Sin prioridad"),
      Asignado_Grupo_Tecnicos: text(row.Asignado_Grupo_Tecnicos, "Sin grupo"),
      Categoria: text(row.Categoria, "Sin categoría"),
      Fecha_Apertura: row.Fecha_Apertura || "",
      Fecha_solucion: row.Fecha_solucion || "",
      Estadisticas_tiempo_solucion_horas: Number.isFinite(number) && number >= 0 ? number : null,
      key: index,
      openingDay: calendarDay(row.Fecha_Apertura),
      solutionDay: calendarDay(row.Fecha_solucion),
      openingTime: timestamp(row.Fecha_Apertura),
      solutionTime: timestamp(row.Fecha_solucion),
    };
    const dateSearch = (day) =>
      day
        ? `${day.slice(8, 10)}/${day.slice(5, 7)}/${day.slice(0, 4)} ${day.slice(
            8,
            10
          )}/${day.slice(5, 7)}/${day.slice(2, 4)}`
        : "";
    ticket.searchText = normalizedText(
      [
        ticket.ID,
        ticket.Titulo,
        ticket.Descripcion,
        ticket.Estado,
        ticket.Prioridad,
        ticket.Asignado_Grupo_Tecnicos,
        ticket.Categoria,
        ticket.Fecha_Apertura,
        ticket.Fecha_solucion,
        ticket.Estadisticas_tiempo_solucion_horas ?? "",
        dateSearch(ticket.openingDay),
        dateSearch(ticket.solutionDay),
      ].join(" ")
    );
    return ticket;
  });
}
export function selectTickets(tickets, filters) {
  const error = validateFilters(filters);
  if (error) throw new Error(error);
  const groups = new Set(filters.grupos);
  return tickets.filter((ticket) => {
    const day = ticket.openingDay;
    return (
      (!filters.fechaInicio || (day && day >= filters.fechaInicio)) &&
      (!filters.fechaFin || (day && day <= filters.fechaFin)) &&
      (!groups.size || groups.has(ticket.Asignado_Grupo_Tecnicos))
    );
  });
}
const add = (map, key) => {
  if (key) map[key] = (map[key] || 0) + 1;
};
export function summarizeTickets(tickets, filters = EMPTY_FILTERS, now = Date.now()) {
  const metrics = {
    total: tickets.length,
    resolved: 0,
    closed: 0,
    pending: 0,
    chronic: 0,
    timed: 0,
    outside: 0,
    hoursSum: 0,
    average: null,
    chronicPercent: 0,
    outsidePercent: null,
    missingOpening: 0,
    missingSolution: 0,
  };
  const states = Object.create(null),
    groups = Object.create(null),
    priorities = Object.create(null),
    categories = Object.create(null),
    months = Object.create(null),
    slaGroups = Object.create(null),
    groupHealth = Object.create(null);
  const history = {
    pending: Object.create(null),
    resolved: Object.create(null),
    closed: Object.create(null),
  };
  for (const ticket of tickets) {
    const group = ticket.Asignado_Grupo_Tecnicos;
    if (!groupHealth[group])
      groupHealth[group] = { pending: 0, chronic: 0, enCourse: 0, timed: 0, outside: 0 };
    const health = groupHealth[group];
    if (ticket.Estado === "En curso") health.enCourse += 1;
    const completed = ticket.Estado === "Resuelto" || ticket.Estado === "Cerrado";
    if (ticket.Estado === "Resuelto") metrics.resolved += 1;
    if (ticket.Estado === "Cerrado") metrics.closed += 1;
    if (!completed) {
      metrics.pending += 1;
      health.pending += 1;
      if (ticket.openingTime !== null && now - ticket.openingTime > 3 * DAY) {
        metrics.chronic += 1;
        health.chronic += 1;
      }
    }
    if (!ticket.openingDay) metrics.missingOpening += 1;
    if (completed && !ticket.solutionDay) metrics.missingSolution += 1;
    add(states, ticket.Estado);
    add(groups, ticket.Asignado_Grupo_Tecnicos);
    const day = ticket.openingDay;
    if (day) {
      const month = day.slice(0, 7);
      if (!months[month]) months[month] = { total: 0, resolved: 0, closed: 0, pending: 0 };
      months[month].total += 1;
      if (ticket.Estado === "Resuelto") months[month].resolved += 1;
      if (ticket.Estado === "Cerrado") months[month].closed += 1;
      if (!completed) months[month].pending += 1;
    }
    const series = !completed ? "pending" : ticket.Estado === "Resuelto" ? "resolved" : "closed";
    const eventDay = completed ? ticket.solutionDay : ticket.openingDay;
    if (eventDay) {
      const month = eventDay.slice(0, 7);
      if (!history[series][month]) history[series][month] = Object.create(null);
      add(history[series][month], group);
    }
    const hours = ticket.Estadisticas_tiempo_solucion_horas;
    if (completed && hours !== null) {
      metrics.timed += 1;
      health.timed += 1;
      metrics.hoursSum += hours;
      if (hours > SLA_HOURS) {
        metrics.outside += 1;
        health.outside += 1;
      }
      for (const [map, key] of [
        [priorities, ticket.Prioridad],
        [categories, ticket.Categoria],
        [slaGroups, ticket.Asignado_Grupo_Tecnicos],
      ]) {
        if (!map[key]) map[key] = { count: 0, total: 0, outside: 0 };
        map[key].count += 1;
        map[key].total += hours;
        if (hours > SLA_HOURS) map[key].outside += 1;
      }
    }
  }
  metrics.average = metrics.timed ? metrics.hoursSum / metrics.timed : null;
  metrics.chronicPercent = metrics.pending ? (100 * metrics.chronic) / metrics.pending : 0;
  metrics.outsidePercent = metrics.timed ? (100 * metrics.outside) / metrics.timed : null;
  return {
    metrics,
    states,
    groups,
    priorities,
    categories,
    months,
    slaGroups,
    history,
    groupHealth,
    calculatedAt: now,
  };
}
const sortField = (ticket, field) => {
  if (field === "Fecha_Apertura") return ticket.openingTime;
  if (field === "Fecha_solucion") return ticket.solutionTime;
  if (field === "ID" && /^\d+$/.test(String(ticket.ID))) return Number(ticket.ID);
  return ticket[field];
};
export function queryTickets(tickets, query = DEFAULT_QUERY) {
  const search = normalizedText(query.search.trim());
  const matches = search ? tickets.filter((ticket) => ticket.searchText.includes(search)) : tickets;
  const sorted = [...matches].sort((a, b) => {
    const av = sortField(a, query.sort),
      bv = sortField(b, query.sort);
    if (av === null || av === "") return bv === null || bv === "" ? a.key - b.key : 1;
    if (bv === null || bv === "") return -1;
    const result =
      typeof av === "number" && typeof bv === "number"
        ? av - bv
        : String(av).localeCompare(String(bv), "es", { numeric: true });
    return (query.direction === "asc" ? result : -result) || a.key - b.key;
  });
  const pageSize = [10, 25, 50, 100].includes(Number(query.pageSize)) ? Number(query.pageSize) : 10;
  const page = Math.min(
    Math.max(0, query.page || 0),
    Math.max(0, Math.ceil(sorted.length / pageSize) - 1)
  );
  return {
    rows: sorted.slice(page * pageSize, (page + 1) * pageSize),
    total: sorted.length,
    page,
    pageSize,
  };
}
export const exportHeaders = [
  "ID",
  "Título",
  "Estado",
  "Prioridad",
  "Grupo técnico",
  "Categoría",
  "Fecha apertura (Bogotá)",
  "Fecha solución (Bogotá)",
  "Tiempo solución (h)",
  "Descripción",
];
const exportDateFormatter = new Intl.DateTimeFormat("es-CO", {
  timeZone: "America/Bogota",
  dateStyle: "short",
  timeStyle: "short",
  hourCycle: "h23",
});
export function exportRow(ticket) {
  const format = (time) => (time === null ? "" : exportDateFormatter.format(new Date(time)));
  return [
    ticket.ID,
    ticket.Titulo,
    ticket.Estado,
    ticket.Prioridad,
    ticket.Asignado_Grupo_Tecnicos,
    ticket.Categoria,
    format(ticket.openingTime),
    format(ticket.solutionTime),
    ticket.Estadisticas_tiempo_solucion_horas ?? "",
    ticket.Descripcion,
  ];
}
export function csvCell(value) {
  const textValue = String(value ?? "");
  const safe = /^[=+\-@\t\r]/.test(textValue) ? `'${textValue}` : textValue;
  return `"${safe.replace(/"/g, '""')}"`;
}
export function summaryRows(summary, filters) {
  const m = summary.metrics;
  return [
    ["Criterio de fecha", "Apertura"],
    ["Desde", filters.fechaInicio || "Sin límite"],
    ["Hasta", filters.fechaFin || "Sin límite"],
    ["Grupos", filters.grupos.join(", ") || "Todos"],
    ["Zona horaria", "America/Bogota"],
    ["Total", m.total],
    ["Resueltos (estado actual)", m.resolved],
    ["Cerrados (estado actual)", m.closed],
    ["Sin resolver", m.pending],
    ["Pendientes > 3 días al consultar", m.chronic],
    ["% de pendientes > 3 días", m.chronicPercent],
    ["Promedio solución (h)", m.average ?? "Sin información"],
    ["Resueltos/cerrados con tiempo válido", m.timed],
    ["Referencia solución (h)", SLA_HOURS],
    ["Fuera de referencia", m.outside],
    ["% fuera de referencia", m.outsidePercent ?? "Sin información"],
  ];
}
